/** Master-P2 Block 2.5 — Orchestrator run detail (persistent DAG view). */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";

function StepStatusBadge({ status }) {
  const cls = {
    pending: "bg-slate-400",
    running: "bg-blue-500",
    blocked: "bg-amber-500",
    done: "bg-emerald-600",
    failed: "bg-red-600",
    skipped: "bg-slate-500",
    superseded: "bg-purple-500",
  }[status] || "bg-slate-400";
  return <Badge className={cls}
                    data-testid={`step-status-${status}`}>{status}</Badge>;
}

function RunStatusBadge({ status }) {
  const cls = {
    pending: "bg-slate-400", running: "bg-blue-500",
    paused: "bg-amber-500", paused_over_limit: "bg-red-500",
    completed: "bg-emerald-600", failed: "bg-red-600",
    cancelled: "bg-slate-500",
  }[status] || "bg-slate-400";
  return <Badge className={cls}
                    data-testid={`run-status-${status}`}>{status}</Badge>;
}

function StepRow({ step: st, onRetry }) {
  const invocations = st.skill_invocations || [];
  return (
    <>
      <tr className="border-b align-top"
            data-testid={`step-row-${st.step_name}`}>
        <td className="p-2 font-mono text-xs">{st.step_name}</td>
        <td className="p-2 text-xs">
          {(st.depends_on || []).join(", ") || "—"}
        </td>
        <td className="p-2"><StepStatusBadge status={st.status} /></td>
        <td className="p-2 text-xs">
          {invocations.length}
          {st.result && st.result.validation_status === "passed" &&
            <span className="ml-1 text-emerald-600"
                    data-testid={`step-validation-passed-${st.step_name}`}>
              Schema ✓
            </span>}
          {st.result && st.result.open_dependency &&
            <div className="text-amber-700 mt-1 font-medium"
                   data-testid={`step-open-dep-${st.step_name}`}>
              ⚠ {st.result.open_dependency}
            </div>}
        </td>
        <td className="p-2 text-xs">
          {(st.status === "failed" || st.status === "blocked") && (
            <Button size="sm" variant="outline"
                      onClick={() => onRetry(st.id)}
                      data-testid={`btn-retry-${st.step_name}`}>
              Retry
            </Button>
          )}
        </td>
      </tr>
      {invocations.length > 0 && (
        <tr className="border-b bg-muted/30"
              data-testid={`step-detail-${st.step_name}`}>
          <td colSpan={5} className="p-2 text-xs">
            <div className="font-medium mb-1">Skill Invocations:</div>
            <table className="w-full">
              <thead className="text-left">
                <tr className="text-muted-foreground">
                  <th className="p-1">invocation_id</th>
                  <th className="p-1">prompt_version</th>
                  <th className="p-1">tool_version</th>
                  <th className="p-1">model</th>
                  <th className="p-1">execution_kind</th>
                  <th className="p-1">latency</th>
                  <th className="p-1">tokens</th>
                </tr>
              </thead>
              <tbody>
                {invocations.map((inv, i) => {
                  const kind = inv.execution_kind || "unknown";
                  const kindCls = {
                    schema_validated_stub: "bg-slate-100 border-slate-300 text-slate-700",
                    deterministic_simulation: "bg-slate-100 border-slate-300 text-slate-700",
                    real_model_call: "bg-emerald-50 border-emerald-300 text-emerald-800",
                    business_verified: "bg-blue-50 border-blue-300 text-blue-800",
                  }[kind] || "bg-slate-100 border-slate-300";
                  return (
                    <tr key={i}>
                      <td className="p-1 font-mono">
                        {(inv.invocation_id || "").slice(0, 8)}…
                      </td>
                      <td className="p-1">{inv.prompt_version || "—"}</td>
                      <td className="p-1">{inv.tool_version || "—"}</td>
                      <td className="p-1">{inv.model || "—"}</td>
                      <td className="p-1">
                        <span className={`text-xs px-1.5 py-0.5 border rounded ${kindCls}`}
                              data-testid={`kind-chip-${st.step_name}`}>
                          {kind === "real_model_call" ? "Real LLM"
                             : kind === "schema_validated_stub" ? "Stub"
                             : kind === "deterministic_simulation" ? "Sim"
                             : kind === "business_verified" ? "Business ✓"
                             : kind}
                        </span>
                      </td>
                      <td className="p-1">{inv.latency_ms}ms</td>
                      <td className="p-1">
                        {inv.tokens_in}/{inv.tokens_out}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

export function OrchestratorRunDetail() {
  const { id } = useParams();
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/orchestrator/runs/${id}`);
      setRun(r.data);
    } catch { toast.error("Nicht gefunden"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function tick() {
    setBusy(true);
    try {
      await api.post(`/orchestrator/runs/${id}/tick`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Fehler");
    }
    setBusy(false);
  }

  async function pause() {
    setBusy(true);
    try {
      await api.post(`/orchestrator/runs/${id}/pause`);
      await load();
    } catch { toast.error("Fehler"); }
    setBusy(false);
  }

  async function resume() {
    setBusy(true);
    try {
      await api.post(`/orchestrator/runs/${id}/resume`);
      await load();
    } catch { toast.error("Fehler"); }
    setBusy(false);
  }

  async function retry(stepId) {
    setBusy(true);
    try {
      await api.post(`/orchestrator/runs/${id}/steps/${stepId}/retry`);
      await load();
    } catch { toast.error("Fehler"); }
    setBusy(false);
  }

  if (!run) return <div className="p-4">Lade …</div>;
  const costPct = Math.min(100, Math.round(
    (100 * (run.cost_used_usd || 0)) / (run.cost_limit_usd || 1)));
  const execPct = Math.min(100, Math.round(
    (100 * (run.steps_executed || 0)) / (run.execution_limit_steps || 1)));

  return (
    <div className="space-y-4" data-testid="orchestrator-run-page">
      <header className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold">Orchestrator-Lauf</h1>
          <p className="text-sm text-muted-foreground">
            {run.id.slice(0, 8)}… · Brief v{run.brief_version} ·
            {" "}<RunStatusBadge status={run.status} />
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={tick} disabled={busy}
                    data-testid="btn-tick">Tick</Button>
          {run.status === "running" && (
            <Button size="sm" variant="outline" onClick={pause} disabled={busy}
                      data-testid="btn-pause">Pause</Button>
          )}
          {(run.status === "paused" || run.status === "paused_over_limit") && (
            <Button size="sm" onClick={resume} disabled={busy}
                      data-testid="btn-resume">Resume</Button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Kosten</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm">
              <span>${(run.cost_used_usd || 0).toFixed(4)} /
                     ${(run.cost_limit_usd || 0).toFixed(2)}</span>
            </div>
            <div className="w-full h-2 bg-muted rounded mt-2 overflow-hidden">
              <div className="h-full bg-blue-500"
                     style={{ width: `${costPct}%` }} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Ausführung</CardTitle></CardHeader>
          <CardContent>
            <div className="text-sm">{run.steps_executed || 0} /
              {" "}{run.execution_limit_steps || 0} Schritte</div>
            <div className="w-full h-2 bg-muted rounded mt-2 overflow-hidden">
              <div className="h-full bg-emerald-500"
                     style={{ width: `${execPct}%` }} />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Schritte ({(run.steps || []).length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-left border-b">
              <tr>
                <th className="p-2">Skill</th>
                <th className="p-2">Abhängig von</th>
                <th className="p-2">Status</th>
                <th className="p-2">Invocations</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {(run.steps || []).map((st) => (
                <StepRow key={st.id} step={st} onRetry={retry} />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <details className="text-xs" data-testid="brief-snapshot-details">
        <summary className="cursor-pointer text-muted-foreground">
          Brief-Snapshot (immutable)
        </summary>
        <pre className="mt-2 p-2 bg-muted rounded overflow-x-auto">
          {JSON.stringify(run.brief_snapshot, null, 2)}
        </pre>
      </details>
    </div>
  );
}

export default OrchestratorRunDetail;
